import { useLogger } from './useLogger';
import { getDefaultTimeApi, getScheduledSlideApi } from '../apis';
import {
  deviceInfoWithAppVersion,
  getContentPath,
  getGeneralPath,
  getTenantId,
  hasKey,
} from '@/utils/functions';
import {
  clearLocalStorage,
  localStorageLabels,
  setCacheService,
  useUtilFunctions,
} from '@/utils';
import BFIT_HORIZONTAL from '../../../assets/ph_horizontal.png';
import BFIT_VERTICAL from '../../../assets/ph_vertical.png';
import moment from 'moment';
import Pusher from 'pusher-js';
import { useAppStateContext } from '@/shared';
import { useImmer } from 'use-immer';
import { useEffect } from 'react';
import { API_ENDPOINT, PUSHER_KEY } from '@/configs';
// import { useProof } from './useProof';
import { useLocation, useNavigate } from 'react-router';
import { useNetwork } from './useNetwork';

export const usePagePlayer = () => {
  const { appState } = useAppStateContext();
  const [state, setState] = useImmer({
    loadingPercent: 0,
    currentIndex: 0,
    isRefreshed: false,
    scheduleChanged: false,
    liveView: false,
    triggerLive: false,
    isBusy: false,
    members: {},
    schedules: [],
    activeItem: [],
    pusher: '',
    timeInfo: {
      UTC: null,
    },
    channelInfo: null,
  });

  useNetwork({ setState });
  const { deviceInfo } = appState;
  const { registerDevice, updateDeviceMeta } = useUtilFunctions();
  const { LogError, LogToExternal } = useLogger();
  // const { addProof } = useProof(); // incomplete feature (hold)

  const navigate = useNavigate();
  const location = useLocation();

  // ================================================================================== API SECTION =================================================================================

  const handleKeyPress = (event) => {
    if (event.keyCode === 413) {
      event.preventDefault();
      navigate('/device/config');
    }
  };

  const triggerLoading = (loading) => {
    setState((draft) => {
      draft.isBusy = loading;
    });
  };

  const getScreenInfo = () => {
    return {
      width: window.screen.width,
      height: window.screen.height,
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      orientation: window.screen.orientation,
    };
  };

  //To get current time for the server
  const getTime = async () => {
    if (!deviceInfo?.timeZone) return;

    try {
      const response = await getDefaultTimeApi({
        timeZone: deviceInfo.timeZone,
      });
      const serverTime = response?.data?.datetime;

      if (serverTime) {
        setState((draft) => {
          draft.timeInfo.UTC = serverTime;
        });
      } else {
        throw response;
      }
    } catch (error) {
      LogToExternal('error', error);
      LogError(1, `Failed to fetch server time: ${error?.message}`);
    }
  };

  const getIdleImage = (orientation) => {
    if (!orientation) return '';

    let idleImage = '';
    const GENERAL_PATH = getGeneralPath(appState.tid);

    const placeholderHorizontal =
      appState?.localData?.config?.placeholderHorizontal;
    const placeholderVertical =
      appState?.localData?.config?.placeholderVertical;

    if (orientation === 1) {
      idleImage = placeholderHorizontal
        ? GENERAL_PATH + placeholderHorizontal
        : BFIT_HORIZONTAL;
    } else {
      idleImage = placeholderVertical
        ? GENERAL_PATH + placeholderVertical
        : BFIT_VERTICAL;
    }

    return idleImage;
  };

  const startPusher = () => {
    try {
      if (!deviceInfo) return;

      // Prevent duplicate connections
      if (state.pusher === 'connected') return;

      // Initialize Pusher
      const pusher = new Pusher(PUSHER_KEY, {
        cluster: 'ap4',
        authEndpoint: API_ENDPOINT + 'pusher/auth',
        auth: {
          params: { deviceId: deviceInfo.deviceId },
        },
      });

      const deviceChannelName = `presence-device-${deviceInfo.deviceId}`;

      // Subscribe to channels
      const channels = {
        public: pusher.subscribe('banjos-signage'),
        presence: pusher.subscribe('presence-banjos-signage'),
        device: pusher.subscribe(deviceChannelName),
      };

      // Handle channel membership changes
      const handleMembershipChange = () => {
        setState((draft) => {
          draft.liveView = hasKey(channels.device.members.members, 'live');
        });
      };

      const handleForceReload = (data) => {
        if (!data || !deviceInfo) return;

        const handleUrlChange = (urlData) => {
          if (!deviceInfo) {
            LogError(
              1,
              `Force Reload: URL Change Failed ${JSON.stringify(urlData)}`,
            );
            return;
          }

          const params = new URLSearchParams({
            tid: urlData.tid,
            accessToken: deviceInfo.accessToken,
            deviceId: deviceInfo.deviceId,
            branchId: deviceInfo.branchId,
            groupId: deviceInfo.groupId,
            timeZone: deviceInfo.timeZone,
            orientation: deviceInfo.orientation,
          });

          window.location.href = `${urlData.URL}/config/?${params.toString()}`;
        };

        switch (data.reloadType) {
          case 0: // Global or timezone-specific reload
            if (
              (data.timeZone === 'All' && data.tid === getTenantId()) ||
              data.timeZone === deviceInfo.timeZone
            ) {
              window.location.reload();
            }
            break;

          case 2: // Device-specific reload
            if (deviceInfo.deviceId === data.deviceId) {
              window.location.reload();
            }
            break;

          case 3: // URL change
            handleUrlChange(data);
            break;

          case 4: // Tenant-specific URL change
            if (
              data.tid === getTenantId() &&
              deviceInfo.deviceId === data.deviceId
            ) {
              handleUrlChange(data);
            }
            break;

          default:
            if (data.timeZone === deviceInfo.timeZone) {
              window.location.reload();
            }
        }

        LogError(0, `Force Reload ${JSON.stringify(data)}`);
      };

      // Event bindings
      const bindEvents = () => {
        channels.device.bind(
          'pusher:subscription_succeeded',
          handleMembershipChange,
        );
        channels.device.bind('pusher:member_added', handleMembershipChange);
        channels.device.bind('pusher:member_removed', handleMembershipChange);

        channels.device.bind('client-live-mode', (data) => {
          setState((draft) => {
            draft.triggerLive = data.status;
          });
        });

        channels.public.bind('force-reload', handleForceReload);
      };

      bindEvents();

      // Store channel info in state
      setState((draft) => {
        draft.channelInfo = channels.device;
      });

      // Track connection state
      pusher.connection.bind('state_change', ({ current }) => {
        setState((draft) => {
          draft.pusher = current;
        });
      });

      return { channels, pusher };
    } catch (error) {
      LogToExternal('error', error);
      LogError(1, `Exception:startPusher ${error.message}`);
    }
  };

  const updateLiveMonitor = () => {
    if (state.liveView) {
      if (state.channelInfo) {
        const { scheduleId, playlist } = state.activeItem;
        const isIdle = scheduleId > 0 ? false : true;
        const playlistName = scheduleId > 0 ? playlist.name : 'idle';
        const config = {
          isIdle,
          playlist: playlistName,
          file:
            scheduleId > 0
              ? playlist.playlistContents[state.currentIndex].file.fileName
              : deviceInfo.orientation === 1
                ? 'idle_hor'
                : 'idle_ver',
          screenInfo: getScreenInfo(),
          thumbnail: deviceInfo.orientation === 1 ? 'idle_hor' : 'idle_ver',
          animation: {
            transitionEntry: playlist?.transitionEntry || 'animate__fadeIn',
            transitionExit: playlist?.transitionExit || 'animate__fadeOut',
          },
        };

        state.channelInfo.trigger('client-live-screen', config);
      }
    }
  };

  const loadContent = async (content) => {
    const contentUrl = getContentPath(getTenantId()) + content.file.fileName;

    try {
      const response = await fetch(contentUrl);
      setCacheService({
        url: contentUrl,
        cacheName: 'contents',
        responseData: response,
      });

      return content;
    } catch (error) {
      LogToExternal('error', error);
      throw content; // rethrow to be caught in Promise.all
    }
  };

  const preloadContents = async (contents) => {
    triggerLoading(true);
    try {
      const preloadTasks = await contents.map(loadContent);
      await Promise.all(preloadTasks);
    } catch (error) {
      LogToExternal('error', error);
    } finally {
      triggerLoading(false);
    }
  };

  const changePlayerContent = () => {
    if (state.activeItem.scheduleId > 0) {
      let currentIndex = state.currentIndex;
      setState((draft) => {
        draft.currentIndex =
          currentIndex < state.activeItem.playlist.playlistContents.length - 1
            ? currentIndex + 1
            : 0;
      });
    }
  };

  const refreshSchedule = async ({ deviceInfo }) => {
    try {
      triggerLoading(true);

      // Get current time first
      await getTime();

      // Handle missing device info
      if (!deviceInfo) {
        const redirectPath =
          !getTenantId() && !location.search ? '/auth/login' : '/device/config';
        navigate(redirectPath, { replace: true });
        return;
      }

      // Handle unregistered devices
      if (!deviceInfo.accessToken) {
        await registerDevice({
          deviceId: deviceInfo.deviceId,
          deviceInfo: deviceInfoWithAppVersion(),
          forceReload: true,
        });
        return;
      }

      // Fetch and process schedule data
      await handleScheduleFetch(deviceInfo);
    } catch (error) {
      handleRefreshError(error);
    } finally {
      triggerLoading(false);
    }
  };

  // Helper function for schedule fetching
  const handleScheduleFetch = async (deviceInfo) => {
    try {
      const response = await getScheduledSlideApi({
        accessToken: deviceInfo.accessToken,
        groupId: deviceInfo.groupId,
      });

      setState((draft) => {
        draft.schedules = response.data;
        draft.isRefreshed = false;
      });

      LogError(0, `Schedule Refreshed ${JSON.stringify(response.data)}`);

      // Update device metadata
      updateDeviceMeta({
        deviceId: deviceInfo.deviceId,
      });
    } catch (error) {
      if (
        (error.response?.data?.message || error.response?.data) ===
        'Device token is invalid'
      ) {
        Promise.resolve(clearLocalStorage(localStorageLabels.DEVICE_INFO)).then(
          () => {
            window.location.reload();
          },
        );
        return;
      }
      throw error; // Re-throw for outer catch block
    }
  };

  const handleRefreshError = (error) => {
    LogToExternal('error', error);
    LogError(1, `Exception:refreshSchedule ${error.message}`);

    setState((draft) => {
      draft.isRefreshed = false;
    });
  };

  // Check deviceInfo
  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    const { channels, pusher } = startPusher({ deviceInfo });
    refreshSchedule({ deviceInfo });

    return () => {
      if (channels && pusher) {
        Object.values(channels).forEach((channel) => {
          channel.unbind_all();
          pusher.unsubscribe(channel.name);
        });
        pusher.disconnect();
      }
    };
  }, []);

  //Preload the contents (Images)
  useEffect(() => {
    if (state.activeItem.scheduleId > 0) {
      preloadContents(state.activeItem.playlist.playlistContents);
    }
  }, [state.activeItem]);

  // Change Player Content
  useEffect(() => {
    try {
      // Send live content to monitor
      updateLiveMonitor();

      if (state.activeItem.scheduleId > 0) {
        const activeContent =
          state.activeItem.playlist.playlistContents[state.currentIndex];
        const duration =
          activeContent.duration > 0
            ? activeContent.duration
            : state.activeItem.playlist.duration;

        // addProof(
        //   deviceInfo.deviceId,
        //   state.activeItem.playlistId,
        //   state.activeItem.playlist.name,
        //   activeContent.file.fileId,
        //   activeContent.file.sourceName,
        //   moment().toISOString(),
        //   moment().add(duration, 'seconds').toISOString(),
        // );

        const interval = setInterval(changePlayerContent, duration * 1000);
        return () => clearInterval(interval);
      }
    } catch (error) {
      LogToExternal('error', error);
      LogError(1, `Exception:ContentChangeHook ${error.message}`);
    }
  }, [state.activeItem, state.currentIndex]);

  useEffect(() => {
    if (state.triggerLive) {
      updateLiveMonitor();
      setState((draft) => {
        draft.triggerLive = false;
      });
    }
    return () => {};
  }, [state.triggerLive]);

  //Set Active Schedule
  useEffect(() => {
    let startTimer;
    let endTimer;

    const handleSchedule = () => {
      try {
        const dateTimeFormat = 'DD-MM-YYYY hh:mm:ss A';
        const timeFormat = 'hh:mm:ss A';

        if (!state.schedules?.length || state.timeInfo.UTC === null) {
          setState((draft) => {
            draft.activeItem = [];
          });
          return;
        }

        const currentUTCMoment = moment(state.timeInfo.UTC, dateTimeFormat);
        const currentDate = currentUTCMoment.format('DD-MM-YYYY');
        const currentTime = currentUTCMoment.format(timeFormat);
        const currentTimeMoment = moment(currentTime, timeFormat);

        // Find first active schedule
        const activeSchedule = state.schedules.find((schedule) =>
          currentTimeMoment.isBefore(moment(schedule.endAt, timeFormat)),
        );

        if (!activeSchedule) {
          setState((draft) => {
            draft.activeItem = [];
          });
          return;
        }

        // Prepare schedule times
        const scheduleStart = `${currentDate} ${activeSchedule.startAt}`;
        const scheduleEnd = `${currentDate} ${activeSchedule.endAt}`;
        const now = `${currentDate} ${currentTime}`;

        // Calculate countdown to schedule start
        const countDown = moment
          .duration(
            moment(scheduleStart, dateTimeFormat).diff(
              moment(now, dateTimeFormat),
            ),
          )
          .asMilliseconds();

        if (countDown > 3000) {
          setState((draft) => {
            draft.activeItem = [];
          });
        }

        // Set timer for schedule start
        startTimer = setTimeout(() => {
          setState((draft) => {
            draft.currentIndex = 0;
            draft.activeItem = activeSchedule;
            draft.scheduleChanged = false;
          });
        }, countDown);

        // Calculate and set timer for schedule end
        const timeLeft = moment
          .duration(
            moment(scheduleEnd, dateTimeFormat).diff(
              moment(now, dateTimeFormat),
            ),
          )
          .asMilliseconds();

        endTimer = setTimeout(() => {
          setState((draft) => {
            draft.currentIndex = 0;
            draft.scheduleChanged = true;
          });
        }, timeLeft);
      } catch (error) {
        LogToExternal('error', error);
        LogError(1, `Exception:SetActiveScheduleHook ${error?.message}`);
      }
    };

    handleSchedule();

    return () => {
      clearTimeout(endTimer);
      clearTimeout(startTimer);
    };
  }, [state.schedules, state.timeInfo.UTC]);

  useEffect(() => {
    state.scheduleChanged && getTime();
  }, [state.scheduleChanged]);

  //Pusher Refresh
  useEffect(() => {
    state.isRefreshed === true && refreshSchedule({ deviceInfo });
  }, [state.isRefreshed]);

  return {
    state,
    getIdleImage,
  };
};
